import type {
  AutoUpdateInputs,
  DatarouterConfig,
  IpJsonContent,
  ScreenArrangement,
} from "@observe-tech/utils";
import { createBrowserCapabilities } from "./browser";
import {
  createElectronCapabilities,
  type ElectronStoreBridge,
} from "./electron";

export type Display = { id: number };

export type ScreensCapability = {
  current(): number;
  displays(): Promise<Display[]>;
  getScreenArrangement(screenIds: number[]): Promise<ScreenArrangement>;
};

export type WindowsCapability = {
  open(screenIds: number[]): Promise<number>;
  focus(screenId: number): Promise<void>;
  connectedScreenIds(): Promise<number[]>;
};

export type FilesCapability = {
  writeIpJson(content: IpJsonContent): Promise<void>;
  removeIpJson(): Promise<void>;
  readLegacyConfig(): Promise<unknown>;
  readLegacyIpJson(): Promise<unknown>;
  onIpJsonEdited?(
    callback: (content: IpJsonContent) => void | Promise<void>,
  ): () => void;
  onReadIpJson?(callback: () => Promise<IpJsonContent | null>): () => void;
};

export type UpdaterCapability = {
  pushAutoUpdateInputs(inputs: AutoUpdateInputs): Promise<void>;
  applyDatarouterConfig(config: DatarouterConfig): Promise<void>;
};

export type HostCapabilities = {
  screens: ScreensCapability;
  windows: WindowsCapability | null;
  files: FilesCapability | null;
  updater: UpdaterCapability | null;
  syncIntegration?(changeType: "site" | "config"): Promise<void>;
};

export const hasWindows = (
  caps: HostCapabilities,
): caps is HostCapabilities & { windows: WindowsCapability } =>
  caps.windows !== null;

export type HostPage = {
  electronAPI?: ElectronStoreBridge;
  location: { href: string };
};

const currentPage = (): HostPage | undefined =>
  typeof window === "undefined"
    ? undefined
    : (window as Window & { electronAPI?: ElectronStoreBridge });

export const createHostCapabilities = (
  page: HostPage | undefined = currentPage(),
): HostCapabilities =>
  page?.electronAPI
    ? createElectronCapabilities(page.electronAPI)
    : createBrowserCapabilities({ href: page?.location.href });

export {
  createBrowserCapabilities,
  createElectronCapabilities,
  type ElectronStoreBridge,
};
